import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

/** One stat line: home/away value, or null when not available yet. */
export type StatPair = { home: number | null; away: number | null; n?: number };
export type MatchStatsDTO = {
  mode: "played" | "averages" | "none";
  source: string | null;
  stats: Record<string, StatPair>;
};

const FIELDS: [string, string, string][] = [
  ["shots", "shots_home", "shots_away"],
  ["shots_on_target", "shots_on_target_home", "shots_on_target_away"],
  ["yellow_cards", "home_yellow_cards", "away_yellow_cards"],
  ["red_cards", "home_red_cards", "away_red_cards"],
  ["saves", "saves_home", "saves_away"],
  ["fouls", "fouls_home", "fouls_away"],
  ["offsides", "offsides_home", "offsides_away"],
  ["corners", "corners_home", "corners_away"],
  ["first_half_corners", "first_half_corners_home", "first_half_corners_away"],
];
const COLS = ["team_home", "team_away", "match_date", "home_score", "data_source", ...FIELDS.flatMap(([, h, a]) => [h, a])].join(", ");

type Row = Record<string, unknown>;
const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : v != null && v !== "" && Number.isFinite(Number(v)) ? Number(v) : null);

/**
 * Public-safe match stats for a pick: real numbers for played matches, each
 * team's last-5 averages for upcoming ones. Missing values stay null — never 0.
 */
export const getMatchStats = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ matchId: z.string().uuid().optional(), home: z.string().min(1).max(120), away: z.string().min(1).max(120) }).parse(d))
  .handler(async ({ data }): Promise<MatchStatsDTO> => {
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      if (data.matchId) {
        const { data: m } = await supabaseAdmin.from("matches").select(COLS).eq("id", data.matchId).maybeSingle();
        const row = m as Row | null;
        if (row && row['home_score'] != null) {
          const stats: Record<string, StatPair> = {};
          let any = false;
          for (const [k, h, a] of FIELDS) {
            const p = { home: num(row[h]), away: num(row[a]) };
            if (p.home != null || p.away != null) any = true;
            stats[k] = p;
          }
          if (any) return { mode: "played", source: (row['data_source'] as string) ?? null, stats };
        }
      }
      const today = new Date().toISOString().slice(0, 10);
      const recent = async (team: string) => {
        const side = (col: "team_home" | "team_away") =>
          supabaseAdmin.from("matches").select(COLS).eq(col, team).not("home_score", "is", null).lt("match_date", today).order("match_date", { ascending: false }).limit(5);
        const [x, y] = await Promise.all([side("team_home"), side("team_away")]);
        return ([...(x.data ?? []), ...(y.data ?? [])] as unknown as Row[])
          .sort((p, q) => String(q['match_date']).localeCompare(String(p['match_date']))).slice(0, 5);
      };
      const [hm, am] = await Promise.all([recent(data.home), recent(data.away)]);
      const avg = (rows: Row[], team: string, h: string, a: string) => {
        const v = rows.map((r) => num(r['team_home'] === team ? r[h] : r[a])).filter((x): x is number => x != null);
        return { val: v.length ? Math.round((v.reduce((s, x) => s + x, 0) / v.length) * 10) / 10 : null, n: v.length };
      };
      const stats: Record<string, StatPair> = {};
      let any = false;
      for (const [k, h, a] of FIELDS) {
        const hh = avg(hm, data.home, h, a), aa = avg(am, data.away, h, a);
        if (hh.val != null || aa.val != null) any = true;
        stats[k] = { home: hh.val, away: aa.val, n: Math.min(hh.n, aa.n) };
      }
      return { mode: any ? "averages" : "none", source: any ? "last 5 matches" : null, stats };
    } catch (e) {
      console.error("match stats failed", e);
      return { mode: "none", source: null, stats: {} };
    }
  });
